import React from 'react';
import Heading from 'grommet/components/Heading';
import Paragraph from 'grommet/components/Paragraph';

export default () => (
  <>
    <Heading>PRIVACY POLICY</Heading>
    <Paragraph>Last Updated: September 2024</Paragraph>
    <Paragraph>
      This Privacy Notice explains what personal data (information) we hold about you, how we collect it, and how we use
      and may share information about you during our management and after it ends. We are required to notify you of this
      information under the General Data Protection Regulation (“GDPR”).
    </Paragraph>
    <Heading tag="h3">1. Who we are</Heading>
    <Paragraph>
      Encore PMG Ltd (the “data controller”) and its subsidiaries (including Encore Estate Management) collects, uses
      and is responsible for certain personal information about you. When we do so, we are regulated under the General
      Data Protection Regulation which applies across the European Union (including in the United Kingdom) and we are
      responsible as “data controller” for that personal information for the purposes of those laws. In this Privacy
      Notice, references to "we" or "us" means Encore PMG Ltd.
    </Paragraph>
    <Heading tag="h3">2. Data protection principles</Heading>

    <Paragraph>
      We will comply with the data protection principles when gathering and using personal information, as set out in
      our GDPR data protection policy.
    </Paragraph>

    <Heading tag="h3">3. The personal information we collect and use: information collected by us</Heading>

    <Paragraph>
      In the course of performing our contractual duties as managing agent, we may collect the following personal
      information when you provide it to us:
    </Paragraph>
    <Paragraph>Name</Paragraph>
    <Paragraph>Correspondence address</Paragraph>
    <Paragraph>Service address</Paragraph>
    <Paragraph>Telephone number</Paragraph>
    <Paragraph>Email address</Paragraph>
    <Paragraph>Bank details</Paragraph>
    <Paragraph>VAT number</Paragraph>
    <Paragraph>Date of birth</Paragraph>
    <Paragraph>Properties owned</Paragraph>
    <Paragraph>CCTV images (where installed)</Paragraph>
    <Paragraph>Physical & Mental Health Information (where provided)</Paragraph>
    <Paragraph>
      The provision of personal data is required from you to enable us to perform our duties depending on your
      relationship with Encore PMG Ltd. We will inform you at the point of collecting information from you whether you
      are required to provide the information to us.
    </Paragraph>

    <Heading tag="h3">4. The personal information we collect and use: information collected from other sources</Heading>
    <Paragraph>We may also obtain personal information from other sources, detailed as follows:</Paragraph>

    <Paragraph>
      Names are obtained from one of the following sources: management agreement, directly supplied via email, online
      website form, database from company acquisition, lease or transfer documents, previous managing agents, AP01 form
      (for new company director appointments), concierge service forms, via letting agents, contractor review form or
      share certificates.
    </Paragraph>

    <Paragraph>
      Correspondence address is obtained from one of the following sources: directly supplied via email, website form,
      database from company acquisition, lease or transfer documents, information received from solicitors or developer
      upon completion, previous managing agents, AP01 form (for new company director appointments), via letting agents
      or contractor review form.
    </Paragraph>

    <Paragraph>
      Service address is obtained from one of the following sources: directly supplied via email, database from company
      acquisition or previous managing agents.
    </Paragraph>

    <Paragraph>
      Telephone number is obtained from one of the following sources: directly supplied via email/phone, website form,
      database from company acquisition, information received from solicitors or developer upon completion, previous
      managing agents, AP01 form (for new company director appointments), concierge service forms, via letting agents or
      contractor review form.
    </Paragraph>

    <Paragraph>
      Email address is obtained from one of the following sources: directly supplied via email, website form, database
      from company acquisition, information received from solicitors or developer upon completion, previous managing
      agents, sales agent moving in form, AP01 form (for new company director appointments), concierge service forms,
      via letting agents or contractor review form
    </Paragraph>

    <Paragraph>
      Bank details are obtained from one of the following sources: directly supplied via email, database from company
      acquisition, previous managing agents or contractor review form.
    </Paragraph>

    <Paragraph>
      VAT number is obtained from one of the following sources: directly supplied via email, database from company
      acquisition, previous managing agents or contractor review form.
    </Paragraph>

    <Paragraph>
      CCTV images are obtained from the following source: CCTV cameras on the estates where there is surveillance
      present.
    </Paragraph>

    <Paragraph>
      Date of birth is obtained from one of the following sources: AP01 form (for new company director appointments).
    </Paragraph>

    <Paragraph>
      Properties owned is obtained from one of the following sources: directly supplied via email, website form,
      database from company acquisition, lease or transfer documents, information received from solicitors or developer
      upon completion, previous managing agents, AP01 form (for new company director appointments) or concierge service
      forms.
    </Paragraph>

    <Paragraph>
      Physical and mental health information is obtained with explicit consent from the following sources: directly
      supplied via email, website form or vulnerable persons questionnaire submission.
    </Paragraph>

    <Heading tag="h3">5. Who we share your personal information with</Heading>

    <Paragraph>
      To perform our contractual duties as managing agent, we may share the above categories of personal data with the
      following categories of recipients:
    </Paragraph>

    <Paragraph>External accountants</Paragraph>

    <Paragraph>Insurance providers</Paragraph>

    <Paragraph>Solicitors</Paragraph>

    <Paragraph>Debt recovery companies</Paragraph>

    <Paragraph>Contractors</Paragraph>

    <Paragraph>Freeholders/landlords</Paragraph>

    <Paragraph>Management Company Directors</Paragraph>

    <Paragraph>Companies House</Paragraph>

    <Paragraph>Technical and emergency support providers</Paragraph>

    <Paragraph>Regulatory bodies for audit purposes</Paragraph>

    <Paragraph>Managing Agents for management handovers</Paragraph>

    <Paragraph>Ombudsman</Paragraph>

    <Paragraph>Bank</Paragraph>

    <Paragraph>ICO for subject access requests and breaches</Paragraph>

    <Paragraph>Emergency services upon request from the police</Paragraph>

    <Paragraph>
      Where possible, the disclosure of recorded CCTV material will only be made to third parties in strict accordance
      with the purposes of the system and is limited to the following;
    </Paragraph>

    <Paragraph>
      Security companies who have been appointed to provide surveillance and security services, in accordance with the
      purposes of the system;
    </Paragraph>

    <Paragraph>Emergency services at the request of the police;</Paragraph>

    <Paragraph>The police.</Paragraph>

    <Paragraph>
      We will share personal information with law enforcement or other authorities if required by applicable law.
    </Paragraph>

    <Heading tag="h3">6. Where your personal information may be held</Heading>

    <Paragraph>
      Information may be held at our offices and those of our group companies, and third party agencies, service
      providers, representatives and agents as described above.
    </Paragraph>

    <Paragraph>
      We have security measures in place to seek to ensure that there is appropriate security for information we hold
      including those measures detailed in our GDPR policy.
    </Paragraph>

    <Heading tag="h3">7. How long your personal information will be kept</Heading>

    <Paragraph>
      We will hold personal information for the duration of management and up to 10 years from the end of management.
    </Paragraph>

    <Paragraph>
      CCTV images will be retained for a period of time as dictated by the system’s recording capability. This period of
      time will vary from system to system. This may mean that images may not always be available when requested via a
      Subject Access Request.
    </Paragraph>

    <Heading tag="h3">8. Reasons we can collect and use your personal information</Heading>

    <Paragraph>We collect and use your personal data on the following lawful bases:</Paragraph>

    <Paragraph>Freeholder/landlord: this is on a contractual basis to fulfil our management duties.</Paragraph>

    <Paragraph>
      Property owners (leaseholder and freeholder): this is on a contractual basis to fulfil our management duties.
    </Paragraph>

    <Paragraph>
      Renters and residents/occupants: this is on a legitimate interest basis for corresponding with whilst in
      residence, to carry out management services and to contact in emergencies.
    </Paragraph>

    <Paragraph>Emergency contacts: this is on a legitimate interest basis to contact in emergencies.</Paragraph>

    <Paragraph>
      Company secretaries (individuals): this is on a legitimate interest basis for corresponding with regarding the
      sale of a property.
    </Paragraph>

    <Paragraph>
      Suppliers/Contractors (sole traders): this is on a contractual basis to send works instructions, process invoices
      and make payments.
    </Paragraph>

    <Paragraph>CCTV: this is on a legitimate interest basis for fulfilling security obligations.</Paragraph>

    <Heading tag="h3">9. Your rights</Heading>

    <Paragraph>
      Under the General Data Protection Regulation you have a number of important rights free of charge. In summary,
      this includes rights to:
    </Paragraph>

    <Paragraph>
      Fair processing of information and transparency regarding how we use your personal information.
    </Paragraph>

    <Paragraph>
      Access to your personal information and to certain other supplementary information that this Privacy Notice is
      already designed to address.
    </Paragraph>

    <Paragraph>Require us to correct any mistakes in your information which we hold.</Paragraph>

    <Paragraph>Require the erasure of personal information concerning you in certain situations.</Paragraph>

    <Paragraph>Object at any time to processing of personal information concerning you for direct marketing.</Paragraph>

    <Paragraph>
      Object to decisions being taken by automated means which produce legal effects concerning you or similarly
      significantly affecting you.
    </Paragraph>

    <Paragraph>Object in certain other situations to our continued processing of your personal information.</Paragraph>

    <Paragraph>Otherwise restrict our processing of your personal information in certain circumstances.</Paragraph>

    <Paragraph>Claim compensation for damages caused by our breach of any data protection laws.</Paragraph>

    <Paragraph>
      For further information on each of those rights, including the circumstances in which they apply, search for the
      Guidance from the UK Information Commissioner’s Office (ICO) on individuals rights under the General Data
      Protection Regulation. If you would like to exercise any of those rights, please:
    </Paragraph>

    <Paragraph>Write to us at: Encore PMG Ltd, 2 Hills Road, Cambridge, CB2 1JP.</Paragraph>

    <Paragraph>
      Let us have enough information to identify you, including your reference number, full name, address, email address
      and name of your estate.
    </Paragraph>

    <Paragraph>
      Let us have proof of your identity and address (a copy of your driving licence or passport and a recent utility or
      credit card bill).
    </Paragraph>

    <Paragraph>
      Let us know the information to which your request relates, including any account or reference numbers if you have
      them.
    </Paragraph>

    <Heading tag="h3">10. Keeping your personal information secure</Heading>

    <Paragraph>
      We have appropriate security measures in place to prevent personal information from being accidentally lost, used
      or accessed in an unauthorised way. We limit access to your personal information to those who have a genuine
      business need to know it. Those processing your information will do so only in an authorised manner and are
      subject to a duty of confidentiality.
    </Paragraph>

    <Paragraph>
      We also have procedures in place to deal with any suspected data security breach. We will notify you and any
      applicable regulator of a suspected data security breach where we are legally required to do so.
    </Paragraph>

    <Heading tag="h3">11. How to complain</Heading>

    <Paragraph>We hope that we can resolve any query or concern you raise about our use of your information.</Paragraph>

    <Paragraph>
      The General Data Protection Regulation also gives you the right to lodge a complaint with a supervisory authority,
      in particular in the European Union (or European Economic Area) state where you work, normally live or where any
      alleged infringement of data protection laws occurred. The supervisory authority in the UK is the Information
      Commissioner who may be contacted at https://ico.org.uk/concerns/.
    </Paragraph>

    <Heading tag="h3">12. Changes to this Privacy Notice</Heading>

    <Paragraph>
      This Privacy Notice may be updated from time to time. The date of the latest update is recorded above.
    </Paragraph>
  </>
);
